import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';

const THEME_COLOR = '#673ab7';
const THEME_BGCOLOR = '#ffffff';

const DemographicsChart = ({ demographicsData }) => {
  // Prepare chart data with differences calculated - MUST be before early return
  const chartData = useMemo(() => {
    if (!demographicsData || !demographicsData.school || !demographicsData.area) {
      return [];
    }

    const categories = [
      { key: 'hispanic', label: 'Hispanic' },
      { key: 'white', label: 'White' },
      { key: 'black', label: 'Black' },
      { key: 'asian', label: 'Asian' }
    ];

    return categories.map(cat => {
      const schoolPct = demographicsData.school[cat.key] || 0;
      const areaPct = demographicsData.area[cat.key] || 0;
      const diff = schoolPct - areaPct;
      
      return {
        category: cat.label,
        school: schoolPct,
        area: areaPct,
        difference: diff,
        absDifference: Math.abs(diff)
      };
    });
  }, [demographicsData]);

  // Find largest difference - MUST be before early return
  const largestDiff = useMemo(() => {
    if (chartData.length === 0) {
      return { category: '', difference: 0, absDifference: 0 };
    }
    return chartData.reduce((max, item) => 
      item.absDifference > max.absDifference ? item : max
    , chartData[0]);
  }, [chartData]);

  // Early return AFTER all hooks
  if (!demographicsData || !demographicsData.school || !demographicsData.area) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: THEME_BGCOLOR,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: THEME_COLOR
          }
        }}
      >
        <Typography color="text.secondary">No demographics data available</Typography>
      </Paper>
    );
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.category}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4338ca', mb: 0.5 }}>
            School: {data.school.toFixed(0)}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#93c5fd', mb: 0.5 }}>
            Area: {data.area.toFixed(0)}%
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: data.difference > 0 ? '#10b981' : data.difference < 0 ? '#ef4444' : '#64748b',
              mt: 0.5,
              pt: 0.5,
              borderTop: '1px solid #e0e0e0'
            }}
          >
            Difference: {data.difference > 0 ? '+' : ''}{data.difference.toFixed(0)}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        backgroundColor: THEME_BGCOLOR,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: THEME_COLOR
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
        <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
          <PeopleIcon />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
          Demographics Comparison (School vs Area)
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 0.5 }}>
        Comparing school demographics to nearby schools average
      </Typography>
      
      <Box sx={{ flex: 1, minHeight: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart 
          data={chartData} 
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="category" 
            tick={{ fontSize: 13 }}
            axisLine={{ stroke: '#e0e0e0' }}
          />
          <YAxis 
            label={{ 
              value: 'Percentage (%)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fontSize: 13, fill: '#64748b' }
            }}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: '#e0e0e0' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="circle"
          />
          <Bar 
            dataKey="school" 
            fill="#4338ca" 
            name="Your School"
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="area" 
            fill="#93c5fd" 
            name="Area Schools"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
      </Box>

      {/* Key Insights */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* Largest difference */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Largest Difference
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {largestDiff.category}
          </Typography>
          <Chip
            label={`${largestDiff.difference > 0 ? '+' : ''}${largestDiff.difference.toFixed(0)}%`}
            size="small"
            sx={{
              mt: 0.5,
              backgroundColor: largestDiff.difference > 0 ? '#dcfce7' : '#fee2e2',
              color: largestDiff.difference > 0 ? '#166534' : '#991b1b',
              fontWeight: 600
            }}
          />
        </Box>

        {/* FRL percentage if available */}
        {demographicsData.school.frl_percent && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Free/Reduced Lunch
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {Math.round(demographicsData.school.frl_percent)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              School students
            </Typography>
          </Box>
        )}
      </Box>

      {/* Interpretation hint */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          <strong>Note:</strong> Positive differences indicate your school has a higher percentage of that demographic group compared to area schools. 
          Negative differences indicate a lower percentage.
        </Typography>
      </Box>
    </Paper>
  );
};

export default DemographicsChart;

